import { createFileRoute, Outlet, redirect, retainSearchParams, stripSearchParams } from "@tanstack/react-router";
import { tenantsListSearchSchema, type TenantsListSearch } from "@/features/tenants/schemas";
import { ApiError } from "@/lib/api-client";
import { fetchSession, sessionQueryKey } from "@/queries/session";
import { AppShell } from "@/components/layout/app-shell";

/**
 * Auth guard layout route.
 *
 * Every authenticated page lives under `/_authed/*`. This route
 * pre-fetches the session via the TanStack Query cache in its
 * `beforeLoad` hook; if the session fetch fails with 401, we throw a
 * redirect to /login. On success, the cached session is available to
 * every child route via `useSessionQuery()` with zero extra network
 * calls.
 *
 * File naming: the leading underscore in `_authed` tells TanStack
 * Router this is a layout route that does NOT add a URL segment. So
 * `_authed/tenants/$slug/applications.tsx` becomes the URL
 * `/tenants/$slug/applications`, but still gets the guard + shell.
 */
export const Route = createFileRoute("/_authed")({
  validateSearch: (search: Record<string, unknown>): { tenantList?: TenantsListSearch } => ({ tenantList: tenantsListSearchSchema.optional().parse(search.tenantList) }),
  search: { middlewares: [
    // Remember an active list while visiting a tenant, without carrying an
    // empty return context into every default link (tenantList=%7B%7D).
    // The outer middleware strips after the inner middleware retains context.
    stripSearchParams({ tenantList: {} }),
    retainSearchParams(["tenantList"]),
  ] },
  beforeLoad: async ({ context, location }) => {
    try {
      // Ensure the session query is in the cache. If it's already there
      // and fresh, this is a no-op; otherwise it fetches and populates.
      await context.queryClient.ensureQueryData({
        queryKey: sessionQueryKey,
        queryFn: fetchSession,
        staleTime: Infinity,
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw redirect({
          to: "/login",
          search: { redirect: location.href },
        });
      }
      // Any other error bubbles up to the router's errorComponent —
      // likely a network outage or backend down.
      throw err;
    }
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
